import React, { useCallback } from "react";
import { observer } from "mobx-react-lite";

import FormField from "../form-field/FormField";
import { DynamicProperty } from "./DynamicProperty";
import { SubSection, DataSection } from "./sections/DataSections";
import { dateService } from "../../services/date-service/DateService";
import { translationService } from "../../services/translation/TranslationService";
import {
  DynamicPropertiesOrganizer,
  PropertiesGrouped,
  PropertiesInColumn,
} from "./DynamicPropertiesOrganizer";
import "./DynamicProperties.scss";
import { secondsToMinutesAndSeconds } from "../../helpers/MathHelpers";

interface DisplayDynamicPropertiesProps {
  properties: DynamicProperty[];
  labelKey?: string;
}
interface DisplayDynamicPropertiesInSectionsProps {
  properties: DynamicProperty[];
  labelKey?: string;
}

interface DisplayDynamicPropertiesInSubSectionsProps {
  properties: DynamicProperty[];
  labelKey?: string;
}

interface DisplayDynamicPropertyProps {
  id: string;
  property: DynamicProperty;
}
interface DisplayDynamicPropertiesInSectionProps {
  labelKey: string | undefined;
  propertyGrouped: PropertiesGrouped;
}

export const DisplayDynamicProperties = (props: DisplayDynamicPropertiesProps) => {
  const { properties, labelKey } = props;
  const propertiesObjs = properties.map((property) => new DynamicProperty(property));

  return (
    <>
      <DisplayDynamicPropertiesInSections labelKey={labelKey} properties={propertiesObjs} />
    </>
  );
};

const DisplayDynamicPropertiesInSections = (props: DisplayDynamicPropertiesInSectionsProps) => {
  const { properties, labelKey } = props;

  const dynamicPropertiesOrganizer = new DynamicPropertiesOrganizer(properties);
  const propertiesGrouped = dynamicPropertiesOrganizer.getGroupedProperties();

  return (
    <div>
      {propertiesGrouped.map((propertyGrouped: PropertiesGrouped, index) => {
        return (
          <DisplayDynamicPropertiesInSection
            key={index}
            propertyGrouped={propertyGrouped}
            labelKey={labelKey}
          />
        );
      })}
    </div>
  );
};

const DisplayDynamicPropertiesInSection = (props: DisplayDynamicPropertiesInSectionProps) => {
  const { propertyGrouped, labelKey } = props;
  return (
    <div className="section">
      <DataSection
        labelKey={propertyGrouped.caption ? undefined : labelKey}
        label={propertyGrouped.caption}
      >
        {propertyGrouped
          .getPropertiesGroupedInColumns()
          .map((propertiesInColumns: PropertiesInColumn, index) => {
            return (
              <DisplaySubSectionWithDynamicProperties
                key={index}
                properties={propertiesInColumns.properties}
              />
            );
          })}
      </DataSection>

      {propertyGrouped.getLongTextProperties().map((property: DynamicProperty) => {
        return (
          <DisplayDynamicProperty
            key={property.metadata.indicator}
            id={property.metadata.indicator}
            property={property}
          />
        );
      })}
      <hr></hr>
    </div>
  );
};

const DisplaySubSectionWithDynamicProperties = (
  props: DisplayDynamicPropertiesInSubSectionsProps
) => {
  const { properties } = props;
  return (
    <SubSection>
      {properties.map((property: DynamicProperty) => (
        <DisplayDynamicProperty
          key={property.metadata.indicator}
          id={property.metadata.indicator}
          property={property}
        />
      ))}
    </SubSection>
  );
};

const DisplayDynamicProperty = observer((props: DisplayDynamicPropertyProps) => {
  const { property } = props;

  const getValue = useCallback(() => {
    if (property.isOfTypeYear()) {
      let dateValue: Date | string = property.getValue();
      return dateValue ? dateService.getYearFromDate(dateValue) : "-";
    }

    if (property.isDate()) {
      const formatString = property.metadata.formatString;
      let dateValue: Date | string | null = property.getValue();
      if (formatString) {
        dateValue = dateService.getDateBasedOnBMFormat(dateValue, formatString);
      }
      return dateValue ? dateService.formatDate(dateValue) : "-";
    }

    if (property.hasCatalog()) {
      return property.getCatalogValue();
    }

    if (property.isBoolean()) {
      const booleanValue = property.getValue();
      if (booleanValue === "0") return translationService.translate("general.no");
      if (booleanValue === "1") return translationService.translate("general.yes");
      return booleanValue;
    }

    if (property.isVideoPositionTime()) {
      const videoPositionTimeValue = parseInt(property.getValue());
      return videoPositionTimeValue || videoPositionTimeValue === 0
        ? secondsToMinutesAndSeconds(videoPositionTimeValue)
        : "-";
    }

    return property.getValue();
  }, [property]);

  return (
    <FormField
      key={property.metadata.indicator}
      id={property.metadata.indicator}
      fieldValue={getValue() || "-"}
      labelKey={property.getLabelText()}
      labelIcon={property.getIssueIcon()}
      readOnly
    />
  );
});
